import { useFeatureFlagEnabled } from "@posthog/react";
import { useMemo } from "react";

import type { DashboardSnapshot } from "../../../lib/dashboard";
import type {
  ManualEntryKind,
  ManualEntryRequest,
  ManualFinancialEntry,
} from "../../../lib/onboarding-api";
import type { UseProfileEditOptions } from "../../../lib/profile/use-profile-edit";
import type { ReadinessResult } from "../../../lib/readiness-types";
import type { ProfileRowId } from "../../../lib/readiness/factor-routing";
import { CreditRow } from "./rows/credit-row";
import { IncomeShapeRow } from "./rows/income-shape-row";
import {
  AggregateInputRow,
  type AggregateInputRowManualEntries,
  type AggregateInputRowProps,
} from "./rows/aggregate-input-row";
import { buildAggregateRows } from "./rows/build-aggregate-rows";
import { sharpenMetaForRow } from "./rows/sharpen-meta";
import { bankComingSoonForRow } from "./inputs-column.helpers";
import type { VerificationFieldCapabilityLookup } from "../../../hooks/useVerificationFieldCapabilities";

// PostHog flag that lifts the income-row "Connect bank — coming soon" pill
// into a live Plaid Link CTA. Cohort/ramp dial for the legacy Bank Income
// re-enable; the backend kill-switch (PLAID_BANK_INCOME_ENABLED) is the
// master on/off. Off/undefined → income row stays "Coming soon".
export const PLAID_BANK_INCOME_FLAG = "plaid_bank_income_enabled";

export interface InputsColumnManualEntries {
  entries: ManualFinancialEntry[];
  isLoading: boolean;
  create: (req: ManualEntryRequest) => Promise<void>;
  update: (
    id: string,
    req: Omit<ManualEntryRequest, "entry_kind">,
  ) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

type EntriesByKind = Record<ManualEntryKind, ManualFinancialEntry[]>;

const EMPTY_ENTRIES_BY_KIND: EntriesByKind = {
  income: [],
  asset: [],
  liability: [],
};

// groupEntriesByKind partitions the entries list once per change so each row
// renders against a stable per-kind slice. Without this, every render filtered
// the array three times and produced a new object identity, breaking any
// downstream memoization.
function groupEntriesByKind(entries: ManualFinancialEntry[]): EntriesByKind {
  const grouped: EntriesByKind = { income: [], asset: [], liability: [] };
  for (const entry of entries) {
    grouped[entry.entry_kind].push(entry);
  }
  return grouped;
}

// Mapping from aggregate row → manual entry kind. Rows not in this map (e.g.
// employment, personal_identity) don't surface manual entries. Income lives
// on the "income" row; cash and assets on "cash_assets"; debts on "debts".
const ROW_TO_KIND: Partial<Record<ProfileRowId, ManualEntryKind>> = {
  income: "income",
  cash_assets: "asset",
  debts: "liability",
};

const ROW_COPY: Partial<
  Record<
    ProfileRowId,
    { sectionLabel: string; addLabel: string; emptyMessage: string }
  >
> = {
  income: {
    sectionLabel: "Income sources",
    addLabel: "Add income manually",
    emptyMessage:
      "Connect accounts, upload documents, or add income manually so Sill can understand your buying power.",
  },
  cash_assets: {
    sectionLabel: "Accounts and assets",
    addLabel: "Add account",
    emptyMessage:
      "Add accounts or assets we don't already see from a connected bank.",
  },
  debts: {
    sectionLabel: "Debts and liabilities",
    addLabel: "Add debt",
    emptyMessage: "Add loans, BNPL, or anything not on a connected account.",
  },
};

function manualEntriesForRow(
  rowId: ProfileRowId,
  manualEntries: InputsColumnManualEntries | undefined,
  entriesByKind: EntriesByKind,
  inlineVerifyHandlers:
    | {
        onConnectBank: (rowId?: ProfileRowId) => void;
        onUploadDocs: () => void;
      }
    | undefined,
  bankIncomeEnabled: boolean,
  // Task 1678 follow-through. These inline CTAs sit beside "+ Add" inside an
  // expanded row and were the one affordance chain the capability gate did
  // not reach. It was harmless only by coincidence: ROW_TO_KIND happens to
  // hold exactly {income, cash_assets, debts}, the same three fields the
  // server says are verifiable. Two independently-maintained sets agreeing
  // by luck is precisely what this task set out to stop relying on.
  capability: { supportsBank: boolean; supportsDocument: boolean },
): AggregateInputRowManualEntries | undefined {
  if (!manualEntries) return undefined;
  const kind = ROW_TO_KIND[rowId];
  const copy = ROW_COPY[rowId];
  if (!kind || !copy) return undefined;
  // Bind rowId at the per-row layer so the parent's `handleVerifyConnectBank`
  // can route income-row clicks to the income-mode Plaid Link hook. Without
  // this bind, the inline Connect bank CTA called the handler with no args
  // and always opened initial mode — silently producing identity+liabilities
  // items even when clicked from the income row. (Observed in prod
  // 2026-05-28; root cause was the parallel `onConnectBank` chain at
  // line 149 binds rowId, but this chain did not.)
  const verifyConnectBank =
    inlineVerifyHandlers && capability.supportsBank
      ? () => inlineVerifyHandlers.onConnectBank(rowId)
      : undefined;
  return {
    kind,
    entries: entriesByKind[kind],
    isLoading: manualEntries.isLoading,
    addLabel: copy.addLabel,
    emptyMessage: copy.emptyMessage,
    sectionLabel: copy.sectionLabel,
    onCreate: manualEntries.create,
    onUpdate: manualEntries.update,
    onDelete: manualEntries.remove,
    // Inline verification CTAs render next to the "+ Add" button so a
    // user editing a row can verify without scrolling back to the
    // section header. Same handlers as the section header — see Phase 1
    // of the 2026-05-19 finances handoff.
    onVerifyConnectBank: verifyConnectBank,
    onVerifyUploadDocs: capability.supportsDocument
      ? inlineVerifyHandlers?.onUploadDocs
      : undefined,
    // Income's Connect bank CTA was deferred 2026-05-28 (Plaid's legacy
    // income_verification product needs a user_token our prod app lacked).
    // 2026-06: Plaid re-enabled user_token on the prod client_id, so the row
    // goes live once the PostHog kill-switch is on — see bankComingSoonForRow.
    bankComingSoon: bankComingSoonForRow(rowId, bankIncomeEnabled),
  };
}


// renderRow wires an aggregate row to its readiness sharpen-pill goal, the
// unified contributor slice (manual + doc-backed + Plaid line items per
// SILL-308), and the per-row Plaid Link "Connect bank" handler (SILL-304).
// The row's `summary` (computed in buildAggregateRows from the snapshot's
// projected scalars) already reflects the full aggregation — manual,
// doc-backed, and Plaid entries flow into the snapshot via the facts
// pipeline (ADR-024 §7), so we don't re-sum them on the client.
function renderRow(
  row: AggregateInputRowProps,
  readiness: ReadinessResult | null | undefined,
  profileEditOpts: UseProfileEditOptions | undefined,
  manualEntries: InputsColumnManualEntries | undefined,
  entriesByKind: EntriesByKind,
  onConnectBank: ((rowId: ProfileRowId) => void) | undefined,
  onUploadDocs: (() => void) | undefined,
  bankConnectLoadingRowId: ProfileRowId | null,
  inlineVerifyHandlers:
    | { onConnectBank: () => void; onUploadDocs: () => void }
    | undefined,
  bankIncomeEnabled: boolean,
  capabilities: VerificationFieldCapabilityLookup,
  onEdit?: (rowId: ProfileRowId) => void,
) {
  const meta = sharpenMetaForRow(row.rowId, readiness);
  // Task 1678: what this row can be verified WITH is the server's answer.
  // Before this, onUploadDocs was forwarded to every row unconditionally.
  const supportsBank = capabilities.supportsBank(row.rowId);
  const supportsDocument = capabilities.supportsDocument(row.rowId);
  return (
    <AggregateInputRow
      key={row.rowId}
      {...row}
      supportsBank={supportsBank}
      supportsDocument={supportsDocument}
      bankReason={capabilities.bankReason(row.rowId)}
      documentReason={capabilities.documentReason(row.rowId)}
      opts={profileEditOpts}
      manualEntries={manualEntriesForRow(
        row.rowId,
        manualEntries,
        entriesByKind,
        inlineVerifyHandlers,
        bankIncomeEnabled,
        { supportsBank, supportsDocument },
      )}
      onConnectBank={
        onConnectBank && supportsBank
          ? () => onConnectBank(row.rowId)
          : undefined
      }
      onUploadDocs={supportsDocument ? onUploadDocs : undefined}
      bankConnectLoading={bankConnectLoadingRowId === row.rowId}
      onEdit={onEdit ? () => onEdit(row.rowId) : undefined}
      sharpenGoal={
        meta
          ? {
              projected_delta: meta.goal.projected_delta,
              action_label: meta.goal.action_label,
              variant: meta.variant,
            }
          : undefined
      }
    />
  );
}

export function InputsColumn({
  snapshot,
  readiness,
  profileEditOpts,
  onConnectBank,
  onUploadDocs,
  capabilities,
  onVerifyConnectBank,
  bankConnectLoadingRowId,
  manualEntries,
  onEdit,
}: {
  profileEditOpts?: UseProfileEditOptions;
  readiness: ReadinessResult | null | undefined;
  snapshot: DashboardSnapshot;
  // SILL-304: per-row BANK pill handler. The TierLadder surfaces the
  // climb intent via onClimb("bank-verified"); the row collapses that
  // into onConnectBank(rowId) here so the page can open a
  // product-scoped Plaid Link session.
  onConnectBank?: (rowId: ProfileRowId) => void;
  // SILL-304: per-row DOC pill handler. Same shape as onConnectBank —
  // the row collapses the TierLadder's "document-backed" climb intent
  // into a navigate-to-documents call so the user lands where they can
  // upload evidence. ALSO reused by the new section-header /
  // per-row-inline InputVerificationCTAs — document upload has no tier
  // gating so the same handler serves both legacy and new placements.
  onUploadDocs?: () => void;
  // 2026-05-19 finances handoff: section-header + per-row inline
  // verification CTA pair (`<InputVerificationCTAs>`). Distinct from
  // `onConnectBank` because this handler is tier-aware at the parent —
  // Plus → Plaid Link, Free → focused `<UpgradeModal>` (not the
  // pricing-comparison `<UpgradeSheet>` the legacy per-row pills hit).
  // Pair with `onUploadDocs` so the CTAs render only when both are
  // wired.
  //
  // rowId is bound at the per-row layer inside InputsColumn so the parent
  // can route income-row clicks to income-mode Plaid Link. Section-header
  // (top-of-section) calls don't pass rowId and default to initial mode.
  onVerifyConnectBank?: (rowId?: ProfileRowId) => void;
  // SILL-304 follow-up: rowId currently being connected (if any). The
  // matching row's BANK pill renders in a loading state so the user
  // sees their click registered during the token-mint + Plaid Link
  // handshake window. Null when no connect is in flight.
  bankConnectLoadingRowId?: ProfileRowId | null;
  // SILL-302: manual entries slice. When present, aggregate rows render
  // an expandable manual-entries section showing the user-entered
  // contributors (income sources, accounts/assets, debts) backed by
  // either user-typed values or accepted document reviews.
  manualEntries?: InputsColumnManualEntries;
  /**
   * When provided, each row shows a pencil edit affordance that calls
   * onEdit(rowId) to open the Edit & Verify drawer. When absent, the
   * existing expand-in-place behavior is used.
   */
  onEdit?: (rowId: ProfileRowId) => void;
  /**
   * Server-owned per-field verification capabilities (task 1678). Supplied
   * by FinancialProfileSection from useVerificationFieldCapabilities.
   *
   * REQUIRED, not optional-with-a-fail-closed-default. Fail-closed is the
   * right behaviour but the wrong place to express it: a production caller
   * that forgot the prop would render a passport offering no verification at
   * all, and nothing would fail to compile. Render-only tests pass
   * `FAIL_CLOSED_CAPABILITIES` explicitly, so the default is stated at the
   * call site that wants it rather than assumed everywhere.
   */
  capabilities: VerificationFieldCapabilityLookup;
}) {
  // buildAggregateRows returns 5 rows in order:
  //   [0] income, [1] employment, [2] cash_assets, [3] debts, [4] personal_identity
  // Per decision §3 ordering: Income → Employment → Cash & Assets → Debts → Credit → Personal & ID
  // We render rows[0..3], then IncomeShapeRow (SILL-537 M5), then CreditRow,
  // then rows[4].
  const rows = buildAggregateRows(snapshot);
  const preCreditRows = rows.slice(0, 4);
  const postCreditRows = rows.slice(4);

  // PostHog cohort gate for the income-row Bank Income CTA. Undefined (no
  // PostHog instance / flag unevaluated) coerces to false → income stays
  // "Coming soon", matching the pre-flag behavior and keeping tests without
  // a PostHog provider green.
  const bankIncomeEnabled = useFeatureFlagEnabled(PLAID_BANK_INCOME_FLAG) ?? false;

  // Partition once per entries-change so each row reads a stable slice. Filter
  // identity matters here even though the input array is small — without
  // memoization, every row got a fresh sub-array on every render, defeating
  // any downstream React.memo on AggregateInputRow / ManualEntryList.
  const entriesByKind = useMemo(
    () =>
      manualEntries
        ? groupEntriesByKind(manualEntries.entries)
        : EMPTY_ENTRIES_BY_KIND,
    [manualEntries],
  );

  // Per-row inline verification CTAs reuse the SAME handlers as the
  // section-header pair so the user gets identical click semantics
  // whether they invoke from the top of the section or next to an
  // "+ Add" button. Only build the bundle when BOTH handlers are wired
  // — a partial pair would be a misconfigured caller, not a partial UI.
  // NOTE: the section-header pair is rendered OUTSIDE InputsColumn (in
  // profile-view.tsx) so it sits inline below the section subheader
  // without the card's surface chrome. Only the per-row inline copy
  // flows through here.
  const inlineVerifyHandlers =
    onVerifyConnectBank && onUploadDocs
      ? { onConnectBank: onVerifyConnectBank, onUploadDocs }
      : undefined;

  return (
    <section
      data-testid="inputs-column"
      aria-label="Inputs"
      className="overflow-hidden"
    >
      <div className="divide-y divide-border-soft">
        {preCreditRows.map((row) =>
          renderRow(
            row,
            readiness,
            profileEditOpts,
            manualEntries,
            entriesByKind,
            onConnectBank,
            onUploadDocs,
            bankConnectLoadingRowId ?? null,
            inlineVerifyHandlers,
            bankIncomeEnabled,
            capabilities,
            onEdit,
          ),
        )}

        <IncomeShapeRow
          snapshot={snapshot}
          opts={profileEditOpts}
          onEdit={onEdit ? () => onEdit("income_shape") : undefined}
        />

        <CreditRow
          snapshot={snapshot}
          readiness={readiness}
          opts={profileEditOpts}
          onEdit={onEdit ? () => onEdit("credit") : undefined}
        />

        {postCreditRows.map((row) =>
          renderRow(
            row,
            readiness,
            profileEditOpts,
            manualEntries,
            entriesByKind,
            onConnectBank,
            onUploadDocs,
            bankConnectLoadingRowId ?? null,
            inlineVerifyHandlers,
            bankIncomeEnabled,
            capabilities,
            onEdit,
          ),
        )}
      </div>
    </section>
  );
}
